import { AddCircleOutlineRounded } from '@mui/icons-material';
import classnames from 'classnames';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import { Dependencies } from 'js/components/pipelines/cards/Dependencies';
import { InfoContent } from 'js/components/pipelines/cards/InfoContent';
import { TopContent } from 'js/components/pipelines/cards/TopContent';
import { ResourceName } from 'js/components/pipelines/cards/components/ResourceName';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { ReactComponent as SendIcon } from 'new/components/icons/fi-sr-send.svg';
import { ReactComponent as SplitIcon } from 'new/components/icons/fi-sr-split.svg';
import { ReactComponent as CropIcon } from 'new/components/icons/fi-sr-tool-crop.svg';
import { ReactComponent as GenAiIcon } from 'new/components/icons/genai.svg';
import { resourceTypes } from 'new/elements/ResourceSearch/ResourceSearch';
import Typography from 'new/elements/Typography';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { useSplitterSupport } from 'new/hooks/useFeatureFlags';
import usePaging from 'new/state/paging/repository/usePaging';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useZoomState } from 'js/components/pipelines/FlowNodes/hooks/useZoomState';
import { LAYOUT } from 'js/components/pipelines/FlowNodes/constants';
import classes from './ResourceCard.module.scss';
import { ActionMenu } from './components/ActionMenu';
import { ActionItem } from './components/ActionMenu/ActionMenu';
import { ExpandActions } from './components/ExpandActions';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { Handle, Position } from '@xyflow/react';
import { SourceNodeFileUpload } from 'js/components/pipelines/FlowNodes/components/SourceNodeFileUpload/SourceNodeFileUpload';
import fileUploadStyles from 'js/components/pipelines/FlowNodes/components/SourceNodeFileUpload/SourceNodeFileUpload.module.scss';
import { Box } from '@mui/material';
import { useResourceCardDragAndDrop } from './hooks/useResourceCardDragAndDrop';
import { UploadIcon } from 'new/components/icons/UploadIcon';

interface ResourceCardProps {
  resourceType: (typeof resourceTypes)[number]['value'];
  expanded?: boolean;
  shouldUseResourceIcon?: boolean;
  InnerView?: ReactNode;
  data?: any;
  isPreview?: boolean;
  isDataFlowStatus?: boolean;
  isFlowRunPreview?: boolean;
  size?: 'x-small' | 'small' | 'normal';
  onClick?: (data: any) => void;
  showTransform?: boolean;
  showSend?: boolean;
  hideActions?: boolean;
  hideNexsetActions?: boolean;
  onClickOrchestrate?: (data: any) => void;
  selectActions?: string[];
  nexset?: any;
  metricData?: any;
  connectionInfo?: {
    iconSm: string;
    name: string;
  };
  status: 'await' | 'normal' | 'emphasis';

  toggleUseExpanded: (id: string, origin_node_id?: string, name?: string) => void;
  splitterNodeProps?: any;
  isNodeDiscoveryEnabled?: boolean;
  canvasScheduleControlsActive?: boolean;
  isMultifileUploadEnabledOnSource?: boolean;
}

const Icon = ({}: Pick<ResourceCardProps, 'status' | 'resourceType' | 'data'>) => {
  return (
    <div className={classes.iconContainer}>
      <AddCircleOutlineRounded />
    </div>
  );
};

const Title = ({
  resourceType,
  nexset,
  data,
}: Pick<ResourceCardProps, 'resourceType' | 'data' | 'nexset'>) => {
  let title = '';
  if (resourceType === RESOURCE_TYPE_SOURCE) {
    title = 'Add Data Source';
  }
  if (resourceType === RESOURCE_TYPE_DATA_SET) {
    title = 'Awaiting data source connection';
    if (data?.id) {
      title = '';
    }
  }
  if (resourceType === RESOURCE_TYPE_SINK) {
    title = 'Add a Destination';
    if (!nexset?.id) {
      title = 'Waiting for nexset detection';
    }
  }
  if (!title) {
    return null;
  }
  return (
    <Typography className={classes.title} variant="body1">
      {title}
    </Typography>
  );
};

const EmptyResourceView = (props: ResourceCardProps) => {
  const { resourceType, data, status, nexset } = props;

  if (data?.id) return null;

  return (
    <div className={classes.emptyViewContainer}>
      <div className={classes.infoContainer}>
        <Icon status={status} resourceType={resourceType} data={data} />
        <Title nexset={nexset} resourceType={resourceType} data={data} />
      </div>
    </div>
  );
};

/*
 * Don't remove this code.
 * It will be used in the future.

const TriggerAction = (props: ResourceCardProps) => {
  const { data, isPreview } = props;
  const [anchor, setAnchor] = useState(null);
  if (data?.type !== 'SOURCE' || isPreview) return null;

  const onClickOptions = (e) => {
    setAnchor(e.currentTarget);
  };

  const onCloseOptions = () => {
    setAnchor(null);
  };

  const onOrchestrate = () => {
    setAnchor(null);
    props.onClickOrchestrate(props.data);
  };

  return (
    <>
      <div onClick={onClickOptions} className={classes.triggerHandleContainer}>
        <div className={classes.triggerActionHandle}>
          <div className={classes.triggerActionContainer}>
            <PlusIcon />
          </div>
        </div>
      </div>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={onCloseOptions}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        <MenuItem onClick={onOrchestrate}>
          <ListItemIcon>
            <CropIcon />
          </ListItemIcon>
          <ListItemText>Orchestrate</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
 */

const NexsetActions = (
  props: ResourceCardProps & {
    onActionMenuStateChange?: (isOpen: boolean) => void;
  },
) => {
  const {
    data,
    isPreview,
    toggleUseExpanded,
    hideActions,
    hideNexsetActions,
    onActionMenuStateChange,
  } = props;
  const [anchor, setAnchor] = useState(null);
  const isSplitterEnabled = useSplitterSupport();

  if (data?.type !== 'DATA_SET' || isPreview) return null;
  if (data?.dataSetType === 'shared' && data.isReference) return null;

  const onClickOptions = (e) => {
    setAnchor(e.currentTarget);
  };

  const onCloseOptions = () => {
    setAnchor(null);
  };

  return (
    <>
      <DataSetDetailActionsContainer data={data} showMenu={false} className={classes.nexsetMenu}>
        {({
          canSend,
          canTransform,
          canGenerate,
          canSplit,
          onClickSend,
          onClickSplit,
          onClickTransform,
        }) => {
          const isActionMenuVisible =
            (canSend || canTransform) && !hideActions && !hideNexsetActions;
          const NexsetActionMenu = isActionMenuVisible ? (
            <ActionMenu
              data-testid="nexset-card-actions"
              pulse={!data.children?.length && !anchor}
              onMenuStateChange={onActionMenuStateChange}
            >
              {canGenerate ? (
                <ActionItem
                  data-testid="nexset-card-actions-generate"
                  icon={
                    <GenAiIcon
                      style={{
                        width: '16px',
                        height: '16px',
                      }}
                    />
                  }
                  onClick={() => {
                    toggleUseExpanded(data.id, data?.origin_node_id, data?.name);

                    setAnchor(null);
                  }}
                >
                  Generate
                </ActionItem>
              ) : null}
              {canTransform ? (
                <ActionItem
                  data-testid="nexset-card-actions-transform"
                  className="__TEST_transform"
                  icon={<CropIcon />}
                  onClick={onClickTransform}
                >
                  Transform
                </ActionItem>
              ) : null}
              {canSend ? (
                <ActionItem
                  data-testid="nexset-card-actions-send"
                  className="__TEST_send"
                  icon={<SendIcon />}
                  onClick={onClickSend}
                >
                  Send to Destination
                </ActionItem>
              ) : null}
              {isSplitterEnabled && canSplit ? (
                <ActionItem className="__TEST_split" icon={<SplitIcon />} onClick={onClickSplit}>
                  Split
                </ActionItem>
              ) : null}
            </ActionMenu>
          ) : null;
          return <>{NexsetActionMenu}</>;
        }}
      </DataSetDetailActionsContainer>
    </>
  );
};

const ResourceContent = (props: ResourceCardProps) => {
  const { data, size, metricData, connectionInfo, isDataFlowStatus, isNodeDiscoveryEnabled } =
    props;
  const { users } = useSelector((state: any) => state.entities);
  const owner = users[data?.owner];
  if (!data?.id) return null;

  return (
    <>
      <TopContent connectionInfo={connectionInfo} {...props} />
      <Dependencies {...props} />
      <InfoContent
        data={data}
        isDataFlowStatus={isDataFlowStatus}
        isNodeDiscoveryEnabled={isNodeDiscoveryEnabled}
        size={size}
        errorCount={isDataFlowStatus ? metricData?.errors : metricData?.metric?.errors}
        recordCount={isDataFlowStatus ? metricData?.records : metricData?.metric?.records}
        recordVolume={isDataFlowStatus ? metricData?.size : metricData?.metric?.size}
        owner={owner}
      />
    </>
  );
};

export const ResourceCard = (props: ResourceCardProps) => {
  const {
    onClick,
    data,
    expanded,
    status,
    size,
    resourceType,
    isDataFlowStatus,
    isFlowRunPreview,
    connectionInfo,
    isPreview,
    splitterNodeProps,
    isMultifileUploadEnabledOnSource,
  } = props;
  const ragFlowId = useRagFlowId();
  const [localExpanded, setLocalExpanded] = useState(expanded);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const { isZoomedOut, hoverScale } = useZoomState();

  const { onExpandNode } = useExpandedNodes();

  const [hasFiles, setHasFiles] = useState(false);

  const fileUploadRef = useRef<{
    handleFileSelect: () => void;
    handleFileDrop: (e: React.DragEvent) => void;
  } | null>(null);

  const { isDragging, dragHandlers } = useResourceCardDragAndDrop({
    isMultifileUploadEnabledOnSource: !!isMultifileUploadEnabledOnSource,
    onFilesDrop: (e) => fileUploadRef.current?.handleFileDrop(e),
  });

  const onClickExpand = () => {
    setLocalExpanded(!localExpanded);
    setTimeout(() => {
      onExpandNode(data.flow_node_id);
    }, 300);
  };

  useDidUpdateEffect(() => {
    setLocalExpanded(expanded);
  }, [expanded]);

  const { getMetaInfoById } = usePaging();
  const metricData = useSelector((state: any) => {
    const metricBase = isDataFlowStatus ? state.metrics.dashboard : state.metrics;
    return metricBase[resourceType]?.[data?.id];
  });

  if (!data) return null;

  const derivedStatus =
    metricData?.status ??
    (isFlowRunPreview && Number(metricData?.metric?.errors) > 0 ? 'ERROR' : undefined);

  const connInfo = connectionInfo || getMetaInfoById(data?.connector_type || data?.connector?.type);

  const onItemClick = (event: React.MouseEvent) => {
    event?.stopPropagation();

    // File upload source: clicking the card opens file selector
    if (isMultifileUploadEnabledOnSource && fileUploadRef.current) {
      const target = event.target as HTMLElement;
      if (target.closest('[data-file-badge]') || target.closest('button')) {
        return;
      }
      fileUploadRef.current.handleFileSelect();
      return;
    }

    if (onClick) {
      onClick(data);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      {ragFlowId ? null : (
        <ExpandActions
          isVisible={!isPreview && !ragFlowId}
          resourceType={resourceType}
          resourceId={data.id}
          data={data}
          canvasScheduleControlsActive={props.canvasScheduleControlsActive}
        />
      )}
      <div
        onClick={onItemClick}
        {...dragHandlers}
        data-testid={`pipeline-origin-card-${data?.id}`}
        data-id={data?.id}
        data-node-type={data?.node_type}
        style={
          isZoomedOut
            ? {
                ['--hover-scale' as string]: hoverScale,
              }
            : undefined
        }
        className={classnames(
          classes.box,
          'ResourceCard-box',
          derivedStatus ? `ResourceCard-box-${derivedStatus}` : undefined,
          classes['box_' + status],
          {
            [classes.box_clickable]: !!onClick || isMultifileUploadEnabledOnSource,
            [classes.box_small]: size === 'small',
            [classes.box_x_small]: size === 'x-small',
            [classes.box_zoomedOut]: isZoomedOut,
            [classes.box_actionMenuOpen]: isActionMenuOpen && isZoomedOut,
            [classes.multifileUploadEnabledOnSource]: isMultifileUploadEnabledOnSource,
            [classes.multifileUploadFileSelected]: hasFiles,
            [fileUploadStyles.cardDragging]: isMultifileUploadEnabledOnSource && isDragging,
          },
          '__TEST_nexset_card',
        )}
      >
        <ResourceName
          {...props}
          {...(splitterNodeProps || {})}
          onClickExpand={onClickExpand}
          expanded={localExpanded}
          resourceType={props.resourceType}
        />
        <EmptyResourceView {...props} />
        <ResourceContent
          {...props}
          expanded={localExpanded}
          metricData={metricData}
          connectionInfo={connInfo}
        />
        <Box className={classnames(classes.dropZoneHint, classes.uploadIconWrapper)}>
          <UploadIcon className={classes.uploadIcon} width="32" height="32" />
          <Typography variant="bodyLarge" className={classes.dropZoneText}>
            Click to upload or drop files here
          </Typography>
        </Box>
        {isMultifileUploadEnabledOnSource && data.id ? (
          <SourceNodeFileUpload
            className={classes.dropZoneContainer}
            ref={fileUploadRef}
            sourceId={data.id}
            isDragging={isDragging}
            onFilesChange={(files) => setHasFiles(files.length > 0)}
          />
        ) : null}
        <NexsetActions {...props} onActionMenuStateChange={setIsActionMenuOpen} />
      </div>
    </div>
  );
};
